import { z } from 'zod';
import { IpAddress, IsoTime, Severity, ServerId } from './basics.js';
import { ServerGroupId, ServerGroupRef } from './server.js';
import { UserId } from './user.js';
import { CountryCode } from './country.js';
import { MitreTactic, MitreTags, MitreTechniqueId } from './mitre.js';
/**
 * Security events as the dashboard reads and triages them (knowledge doc sections
 * 15, 16 and 17).
 *
 * `GuardEvent` in `event.ts` is the *agent's* shape — what a module produces and
 * ingest stores. These are the central side of the same row: the server it belongs
 * to resolved to a name, the assignee resolved to an address, and the triage fields
 * (`acknowledged`, `note`, `assignedTo`) that only the API and the console ever
 * write. Two shapes rather than one, for the reason `server.ts` gives: a page of
 * two hundred events must not carry two hundred copies of `raw`.
 */
/**
 * Section 6: "a plain string: `bruteforce`, `fim`, `disk`, `db`, `exposure`,
 * `agent`, …". The ellipsis is load-bearing, so this is a suggestion list for the
 * filter box and never a validation rule — an agent newer than this API must be
 * able to name a category the console has not heard of.
 */
export const KNOWN_EVENT_CATEGORIES = [
    'agent',
    'bruteforce',
    'db',
    'disk',
    'exposure',
    'fim',
    'inventory',
    'rootcheck',
    'sca',
    'traffic',
    'vulnerability',
];
/**
 * Long enough for what happened and what was done about it, short enough that the
 * column is not an unbounded place to put things (rule 14's spirit: nothing written
 * without a size limit).
 */
export const EVENT_NOTE_MAX_LENGTH = 2000;
export const EVENT_PAGE_DEFAULT = 50;
export const EVENT_PAGE_MAX = 200;
/**
 * Who is looking at an event.
 *
 * The address rather than the id alone, because a table showing `018f2b1c-…` helps
 * nobody. It is visible to a viewer, which is a deliberate call: on a fleet console
 * the assignee is a colleague, and the same is already true of a token's
 * `createdByEmail`.
 */
export const EventAssignee = z.object({
    id: UserId,
    email: z.email(),
});
/** One row in the events table. */
export const SecurityEventSummary = z.object({
    /** The agent's UUIDv7, which is also the deduplication key (section 16). */
    id: z.uuidv7(),
    serverId: ServerId,
    /** Resolved here so the table does not need a second request per row. */
    serverName: z.string().min(1).max(255),
    category: z.string().min(1).max(32),
    severity: Severity,
    message: z.string().min(1).max(500),
    sourceIp: IpAddress.nullable(),
    path: z.string().max(4096).nullable(),
    actionTaken: z.string().max(200).nullable(),
    occurredAt: IsoTime,
    /**
     * When the API stored it. Worth showing beside `occurredAt`: a wide gap is a
     * replayed buffer, which is the agent working as section 14 intends rather than
     * an event that has been sitting unnoticed.
     */
    receivedAt: IsoTime,
    acknowledged: z.boolean(),
    assignee: EventAssignee.nullable(),
    /** So a row can show that somebody wrote something without carrying the text. */
    hasNote: z.boolean(),
    /** The Wazuh rule's MITRE ATT&CK tags (P11.8.6); null for every other event. */
    mitre: MitreTags.nullable().default(null),
    /** From CrowdSec's own lookup, when it raised the alert (P11.11); null otherwise. */
    country: CountryCode.nullable().default(null),
});
export const SecurityEventDetail = SecurityEventSummary.extend({
    note: z.string().max(EVENT_NOTE_MAX_LENGTH).nullable(),
    /**
     * What CrowdSec or Wazuh actually produced. Ingest caps its stored size, so this
     * is bounded by the time it is read; it is `unknown` because its shape belongs to
     * the source and not to us.
     */
    raw: z.unknown(),
    /** The server's group, so triage can see "this is a web server" without a hop. */
    group: ServerGroupRef.nullable(),
});
/**
 * Section 15: "Filter by server, group, category, severity, time; paginated."
 *
 * Severity is a **minimum** rather than an exact match, because "high and above" is
 * the question an operator actually asks and section 16 already uses that shape for
 * `notification_rules.min_severity`. Every other filter is exact.
 */
export const EventListQuery = z
    .object({
    server_id: ServerId.optional(),
    group_id: ServerGroupId.optional(),
    category: z.string().trim().min(1).max(32).optional(),
    /** A MITRE ATT&CK tactic (`Defense Evasion`) or technique id (`T1070.004`), P11.8.6. */
    tactic: MitreTactic.optional(),
    technique: MitreTechniqueId.optional(),
    min_severity: Severity.optional(),
    /** Unset shows both; `false` is the triage queue. */
    acknowledged: z
        .enum(['true', 'false'])
        .optional()
        .transform((value) => (value === undefined ? undefined : value === 'true')),
    /**
     * `me` is resolved against the session rather than by the browser sending its
     * own id, so a bookmarked "my queue" URL means the right thing for whoever
     * opens it. `none` is the unclaimed pile.
     */
    assigned_to: z.union([z.literal('me'), z.literal('none'), UserId]).optional(),
    /** Inclusive; `to` is exclusive, so adjacent ranges neither overlap nor gap. */
    from: IsoTime.optional(),
    to: IsoTime.optional(),
    limit: z.coerce.number().int().min(1).max(EVENT_PAGE_MAX).default(EVENT_PAGE_DEFAULT),
    cursor: z.string().max(512).optional(),
})
    .refine((query) => query.from === undefined ||
    query.to === undefined ||
    Date.parse(query.from) < Date.parse(query.to), { message: '`from` must be before `to`', path: ['from'] });
/**
 * Section 17 gives acknowledging and assigning to an operator, and section 15 puts
 * all three on one `PATCH /events/:id`.
 *
 * `assignedTo` carries a user id rather than only "me", so the contract is complete
 * before the console can use all of it: P8.3's `/users` is what gives React a
 * roster to pick from, and until then it offers claiming and releasing an event.
 *
 * Every field is optional and at least one must be present, for the reason
 * `UpdateServerRequest` gives: a PATCH that changes nothing should be a mistake the
 * schema catches rather than an audit entry saying nothing happened.
 */
export const UpdateEventRequest = z
    .object({
    acknowledged: z.boolean().optional(),
    /** An empty note means removing it, so it is stored as null rather than as ''. */
    note: z
        .string()
        .trim()
        .max(EVENT_NOTE_MAX_LENGTH)
        .nullable()
        .transform((value) => (value === null || value.length === 0 ? null : value))
        .optional(),
    /** `null` releases the event back to the unclaimed pile. */
    assignedTo: UserId.nullable().optional(),
})
    .refine((value) => Object.values(value).some((field) => field !== undefined), 'change at least one field');
//# sourceMappingURL=security-event.js.map